import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import api from '../../services/api';
import toast from 'react-hot-toast';
import type { OrderRecord } from '../../types/orders';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orders: OrderRecord[];
}

// Helper function to parse product_short_description
function parseProducts(description: string | null | undefined): Array<{ sku: string; name: string; isCustom: boolean }> {
  if (!description) return [];

  const products = description.split(',').map(item => item.trim());

  return products.map(product => {
    // Format: "SKU - Name" lub "Custom Name (description)"
    if (product.includes(' - ')) {
      const [sku, name] = product.split(' - ').map(s => s.trim());
      return { sku, name, isCustom: false };
    } else {
      // Custom product
      return { sku: 'CUSTOM', name: product, isCustom: true };
    }
  });
}

export const OrderDetailsModal = ({ isOpen, onClose, orders }: OrderDetailsModalProps) => {
  const [editedOrder, setEditedOrder] = useState<OrderRecord | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (orders && orders.length > 0) {
      setEditedOrder({ ...orders[0] });
    }
  }, [orders]);

  if (!orders || orders.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Szczegóły zamówień" size="xl">
        <div className="text-center py-8 text-gray-500">
          Brak zamówień przypisanych do tego zlecenia transportowego
        </div>
      </Modal>
    );
  }

  const products = parseProducts(editedOrder?.product_short_description);

  const handleSave = async () => {
    if (!editedOrder) return;

    try {
      setSaving(true);
      await api.put(`/orders/${editedOrder.id}`, editedOrder);
      toast.success('Zamówienie zaktualizowane');
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Nie udało się zapisać zmian');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof OrderRecord, value: any) => {
    if (!editedOrder) return;
    setEditedOrder({ ...editedOrder, [field]: value });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Szczegóły i edycja zamówienia`} size="xl">
      {editedOrder && (
        <div className="space-y-6">
          {/* Podstawowe informacje */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nr zamówienia</label>
              <input
                type="text"
                value={editedOrder.order_nr || ''}
                onChange={(e) => handleChange('order_nr', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nr PO klienta</label>
              <input
                type="text"
                value={editedOrder.client_po || ''}
                onChange={(e) => handleChange('client_po', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data zamówienia</label>
              <input
                type="date"
                value={editedOrder.order_date || ''}
                onChange={(e) => handleChange('order_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa ref.</label>
              <input
                type="text"
                value={editedOrder.ref_name || ''}
                onChange={(e) => handleChange('ref_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Handlowiec</label>
              <input
                type="text"
                value={editedOrder.responsible || ''}
                onChange={(e) => handleChange('responsible', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tydzień dostawy</label>
              <input
                type="text"
                value={editedOrder.requested_delivery_week || ''}
                onChange={(e) => handleChange('requested_delivery_week', e.target.value)}
                placeholder="np. 2025-W44"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Produkty - Kafelki */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Produkty</label>
            {products.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                Brak produktów w tym zamówieniu
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {products.map((product, idx) => (
                  <div
                    key={idx}
                    className={`relative rounded-lg p-4 min-w-[220px] ${
                      product.isCustom
                        ? 'bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-300'
                        : 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300'
                    }`}
                  >
                    {product.isCustom && (
                      <div className="absolute -top-2 -left-2 bg-purple-600 text-white rounded-full px-2 py-0.5 text-xs font-bold">
                        CUSTOM
                      </div>
                    )}

                    <div className="text-xs text-gray-500 uppercase mb-1">Nazwa:</div>
                    <div className="text-sm text-gray-900 font-medium mb-2">
                      {product.name}
                    </div>

                    {!product.isCustom && (
                      <>
                        <div className="text-xs text-gray-500 uppercase mb-1">SKU:</div>
                        <div className="font-medium text-sm text-blue-900">{product.sku}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Daty */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Termin załadunku</label>
              <input
                type="date"
                value={editedOrder.confirmed_collection_date || ''}
                onChange={(e) => handleChange('confirmed_collection_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Szac. dostawa</label>
              <input
                type="date"
                value={editedOrder.estimated_delivery_date || ''}
                onChange={(e) => handleChange('estimated_delivery_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Adres dostawy */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adres dostawy</label>
            <textarea
              value={editedOrder.delivery_address || ''}
              onChange={(e) => handleChange('delivery_address', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Wymagania specjalne */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Wymagania specjalne</label>
            <textarea
              value={editedOrder.special_requirements || ''}
              onChange={(e) => handleChange('special_requirements', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Anuluj
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? 'Zapisywanie...' : 'Zapisz zmiany'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
